import { useState } from "react";
import { Link } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import { Leaf, Minus, Plus, ShoppingBag, Tag, X } from "lucide-react";
import { useCart } from "@/context/CartContext";
import { apiPost, money } from "@/lib/api";

export function CouponBox({ compact = false }) {
    const { items, coupon, setCoupon } = useCart();
    const [code, setCode] = useState("");
    const [busy, setBusy] = useState(false);
    const [message, setMessage] = useState(null);

    const minimalItems = items.map(({ product_id, variation_id, quantity }) => ({
        product_id,
        variation_id,
        quantity,
    }));

    const apply = async () => {
        if (!code.trim() || busy) return;
        setBusy(true);
        setMessage(null);
        try {
            const res = await apiPost("/coupon/apply", { code: code.trim(), items: minimalItems });
            setCoupon({ code: res.code, discount: res.discount, total: res.total, deferred: !!res.deferred });
            setMessage({ ok: true, text: res.message });
            setCode("");
        } catch (e) {
            setCoupon(null);
            setMessage({ ok: false, text: e.message });
        } finally {
            setBusy(false);
        }
    };

    return (
        <div data-testid="coupon-box">
            <div className="flex gap-2">
                <div className="relative flex-1">
                    <Tag size={14} className="absolute left-4 top-1/2 -translate-y-1/2 text-sage" />
                    <input
                        data-testid="coupon-input"
                        value={code}
                        onChange={(e) => setCode(e.target.value)}
                        onKeyDown={(e) => e.key === "Enter" && apply()}
                        placeholder="Coupon code"
                        className="w-full rounded-full border border-leaf/20 bg-moss/60 py-3 pl-10 pr-4 text-sm text-bone placeholder:text-sage/70 focus:outline-none focus:border-leaf/60"
                    />
                </div>
                <button
                    data-testid="coupon-apply"
                    onClick={apply}
                    disabled={busy || !code.trim()}
                    className="rounded-full border border-leaf/40 px-5 text-sm font-semibold text-leaf-bright hover:bg-leaf/10 transition-colors disabled:opacity-40"
                >
                    {busy ? "..." : "Apply"}
                </button>
            </div>
            {coupon && (
                <p className="mt-2 text-xs text-leaf-bright" data-testid="coupon-applied-note">
                    {coupon.deferred
                        ? `${coupon.code} attached — verified when your order is placed`
                        : `${coupon.code} applied — saving ${money(coupon.discount)}`}
                </p>
            )}
            {message && (
                <p className={`mt-2 text-xs ${message.ok ? "text-leaf-bright" : "text-red-400"}`} data-testid="coupon-message">
                    {message.text}
                </p>
            )}
        </div>
    );
}

export function QtyStepper({ line, testidPrefix = "cart" }) {
    const { setQty } = useCart();
    return (
        <div className="flex items-center gap-1 rounded-full border border-leaf/20 bg-moss/60">
            <button
                data-testid={`${testidPrefix}-qty-minus`}
                onClick={() => setQty(line.key, line.quantity - 1)}
                className="flex h-8 w-8 items-center justify-center rounded-full text-bone/80 hover:text-leaf-bright"
                aria-label="Decrease quantity"
            >
                <Minus size={13} />
            </button>
            <span data-testid={`${testidPrefix}-qty`} className="w-6 text-center text-sm font-bold">
                {line.quantity}
            </span>
            <button
                data-testid={`${testidPrefix}-qty-plus`}
                onClick={() => setQty(line.key, line.quantity + 1)}
                disabled={line.quantity >= (line.max_qty || 10)}
                className="flex h-8 w-8 items-center justify-center rounded-full text-bone/80 hover:text-leaf-bright disabled:opacity-30"
                aria-label="Increase quantity"
            >
                <Plus size={13} />
            </button>
        </div>
    );
}

function CartLine({ line, light = false }) {
    const { removeItem } = useCart();
    return (
        <div className="flex gap-4 py-4" data-testid="cart-line">
            <div className="h-20 w-20 shrink-0 overflow-hidden rounded-xl border border-leaf/15 bg-moss">
                {line.image ? (
                    <img src={line.image} alt={line.name} className="h-full w-full object-cover" />
                ) : (
                    <div className="flex h-full w-full items-center justify-center text-leaf/50">
                        <Leaf size={20} />
                    </div>
                )}
            </div>
            <div className="flex flex-1 flex-col justify-between min-w-0">
                <div className="flex items-start justify-between gap-3">
                    <div className="min-w-0">
                        <Link to={`/product/${line.slug}`} className="font-display font-bold text-sm leading-snug hover:text-leaf-bright transition-colors line-clamp-2">
                            {line.name}
                        </Link>
                        {line.options?.length > 0 && (
                            <p className="mt-1 text-xs text-sage">{line.options.map((o) => `${o.name}: ${o.value}`).join(" · ")}</p>
                        )}
                    </div>
                    <button
                        data-testid="cart-line-remove"
                        onClick={() => removeItem(line.key)}
                        className="text-sage hover:text-red-400 transition-colors"
                        aria-label={`Remove ${line.name}`}
                    >
                        <X size={16} />
                    </button>
                </div>
                <div className="flex items-center justify-between gap-3">
                    <QtyStepper line={line} />
                    <div className="text-right">
                        {line.on_sale && line.regular_unit_price > line.unit_price && (
                            <span className="mr-2 text-xs text-sage line-through">{money(line.regular_unit_price)}</span>
                        )}
                        <span className={`font-bold ${line.on_sale ? "text-leaf-bright" : "text-bone"}`}>
                            {money(line.unit_price * line.quantity)}
                        </span>
                    </div>
                </div>
            </div>
        </div>
    );
}

export default function CartDrawer() {
    const { drawerOpen, setDrawerOpen, items, subtotal, discount, total, count, coupon } = useCart();

    return (
        <AnimatePresence>
            {drawerOpen && (
                <>
                    <motion.div
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                        onClick={() => setDrawerOpen(false)}
                        className="fixed inset-0 z-[80] bg-ink/70 backdrop-blur-sm"
                        data-testid="cart-drawer-backdrop"
                    />
                    <motion.aside
                        initial={{ x: "100%" }}
                        animate={{ x: 0 }}
                        exit={{ x: "100%" }}
                        transition={{ type: "spring", damping: 30, stiffness: 300 }}
                        className="fixed right-0 top-0 z-[90] flex h-full w-full max-w-md flex-col border-l border-leaf/20 bg-ink/92 backdrop-blur-xl"
                        data-testid="cart-drawer"
                    >
                        <div className="flex items-center justify-between border-b border-leaf/15 px-6 py-5">
                            <h2 className="font-display text-xl font-extrabold">
                                Your Bag <span className="text-leaf-bright">({count})</span>
                            </h2>
                            <button
                                data-testid="cart-drawer-close"
                                onClick={() => setDrawerOpen(false)}
                                className="flex h-9 w-9 items-center justify-center rounded-full border border-leaf/20 text-bone hover:border-leaf/50"
                                aria-label="Close cart"
                            >
                                <X size={16} />
                            </button>
                        </div>

                        {items.length === 0 ? (
                            <div className="flex flex-1 flex-col items-center justify-center gap-5 px-8 text-center">
                                <div className="flex h-16 w-16 items-center justify-center rounded-full border border-leaf/25 text-leaf">
                                    <ShoppingBag size={24} />
                                </div>
                                <p className="font-display text-xl font-bold text-bone">Your bag is empty</p>
                                <p className="text-sm text-sage">The menu is stacked — go find something good.</p>
                                <Link
                                    to="/shop"
                                    data-testid="cart-drawer-shop-cta"
                                    onClick={() => setDrawerOpen(false)}
                                    className="mt-2 rounded-full bg-leaf px-8 py-3.5 font-bold text-ink hover:bg-leaf-bright transition-colors"
                                >
                                    Shop the Menu
                                </Link>
                            </div>
                        ) : (
                            <>
                                <div className="flex-1 overflow-y-auto px-6 divide-y divide-leaf/10">
                                    {items.map((line) => (
                                        <CartLine key={line.key} line={line} />
                                    ))}
                                </div>
                                <div className="border-t border-leaf/15 px-6 py-5 space-y-4">
                                    <CouponBox />
                                    <div className="space-y-1.5 text-sm">
                                        <div className="flex justify-between text-sage">
                                            <span>Subtotal</span>
                                            <span data-testid="cart-drawer-subtotal">{money(subtotal)}</span>
                                        </div>
                                        {discount > 0 && (
                                            <div className="flex justify-between text-leaf-bright" data-testid="cart-drawer-discount">
                                                <span>Discount</span>
                                                <span>-{money(discount)}</span>
                                            </div>
                                        )}
                                        <div className="flex justify-between font-display text-lg font-extrabold text-bone pt-1">
                                            <span>Total</span>
                                            <span data-testid="cart-drawer-total">{money(total)}</span>
                                        </div>
                                    </div>
                                    <Link
                                        to="/checkout"
                                        data-testid="cart-drawer-checkout"
                                        onClick={() => setDrawerOpen(false)}
                                        className="block w-full rounded-full bg-leaf py-4 text-center font-bold text-ink hover:bg-leaf-bright transition-colors"
                                    >
                                        Checkout · {money(total)}
                                    </Link>
                                    <button
                                        data-testid="cart-drawer-view-cart"
                                        onClick={() => setDrawerOpen(false)}
                                        className="w-full text-center text-xs font-mono uppercase tracking-widest text-sage hover:text-leaf-bright"
                                    >
                                        <Link to="/cart" onClick={() => setDrawerOpen(false)}>View full cart</Link>
                                    </button>
                                    {coupon && discount > 0 && null}
                                </div>
                            </>
                        )}
                    </motion.aside>
                </>
            )}
        </AnimatePresence>
    );
}
